/**
 * Realtor Home dashboard.
 *
 * Once the realtor's profile is loaded, parallel-fetches listings + leads for
 * the listing/pipeline tiles and the hot-listings grid. The loaded view then
 * asks the schedule endpoint (every open scheduled activity, not one per lead)
 * for two windows:
 *   - everything open before local midnight tonight → "Visits & calls" (split
 *     into overdue and today) and the "Reminders due" tile;
 *   - now → now + 7 days → the "Scheduled visits" tile and "Coming up".
 * The enquiries inbox loads open enquiries on its own, and the Follow-ups
 * queue above it loads the realtor's nudges on its own.
 *
 * The verification banner stays for unverified realtors. Everything above
 * runs even when unverified — only publishing is gated. Under it, a banner
 * shows when the WhatsApp connection stopped recording. Above the follow-ups,
 * a card links to the WhatsApp chats waiting for review.
 */

import {
	AlertCircle,
	BellRing,
	Home,
	Layers,
	Plus,
	RefreshCw,
	ShieldCheck,
	Sparkles,
	UserRound,
	Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	countFollowUpsDue,
	countVisits,
	dashboardRanges,
	hotListings,
	isOpenLead,
} from "@/features/realtor/components/dashboard/dashboard-data";
import { EnquiriesInbox } from "@/features/realtor/components/dashboard/enquiries-inbox";
import {
	ComingUp,
	TodaySchedule,
} from "@/features/realtor/components/dashboard/schedule-widgets";
import {
	EYEBROW_STYLE,
	SectionHeading,
} from "@/features/realtor/components/dashboard/section-heading";
import { WhatsappConnectionBanner } from "@/features/realtor/components/dashboard/whatsapp-connection-banner";
import { WhatsappReviewCard } from "@/features/realtor/components/dashboard/whatsapp-review-card";
import { FollowUpsQueue } from "@/features/realtor/components/follow-ups/follow-ups-queue";
import { ListingCard } from "@/features/realtor/components/listing-card";
import { useSchedule } from "@/features/realtor/hooks/use-realtor-crm";
import {
	type LeadWithLinks,
	listMyLeads,
} from "@/features/realtor/services/leads-api.service";
import {
	type Listing,
	listMyListings,
} from "@/features/realtor/services/listings-api.service";
import {
	getMyRealtorProfile,
	type RealtorProfile,
} from "@/features/realtor/services/realtor-api.service";

// Three orthogonal states the dashboard can be in:
//   - loading: in-flight fetch
//   - error: profile fetch failed (network, 5xx, etc.) — distinct from "no profile"
//   - empty: 200 OK with profile=null — realtor needs to complete onboarding
//   - loaded: profile present (listings/leads are best-effort, not blocking)
type DashboardState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| { status: "empty" }
	| {
			status: "loaded";
			profile: RealtorProfile;
			listings: Listing[];
			leads: LeadWithLinks[];
	  };

const ACTIVE_LISTING_STATUSES: Listing["status"][] = [
	"active",
	"under_negotiation",
];

export function RealtorDashboard() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const user = useAuthStore((state) => state.user);
	const [state, setState] = useState<DashboardState>({ status: "loading" });
	// Single ref shared by initial load + retry; flipped in the
	// effect's cleanup so a retry-then-unmount can't fire setState on
	// an unmounted component.
	const unmountedRef = useRef(false);

	const load = useCallback(async () => {
		setState({ status: "loading" });
		try {
			const profileResult = await getMyRealtorProfile();
			if (unmountedRef.current) return;
			if (!profileResult.success) {
				setState({
					status: "error",
					message:
						profileResult.error ?? t("realtor.dashboard.loadFailedFallback"),
				});
				return;
			}
			if (!profileResult.profile) {
				setState({ status: "empty" });
				return;
			}

			// Listings + leads are independent — fetch in parallel. If either
			// fails we degrade to an empty list rather than blocking the entire
			// dashboard; the welcome card + verification banner still render.
			// We DO toast the failure though, so a realtor doesn't see "0
			// active listings" and assume their data vanished.
			const [listingsResult, leadsResult] = await Promise.all([
				listMyListings(),
				listMyLeads(),
			]);
			if (unmountedRef.current) return;

			if (!listingsResult.success) {
				toast.error(
					listingsResult.error ?? t("realtor.dashboard.listingsLoadFailed"),
				);
			}
			if (!leadsResult.success) {
				toast.error(
					leadsResult.error ?? t("realtor.dashboard.leadsLoadFailed"),
				);
			}

			setState({
				status: "loaded",
				profile: profileResult.profile,
				listings: listingsResult.success ? (listingsResult.data ?? []) : [],
				leads: leadsResult.success ? (leadsResult.data ?? []) : [],
			});
		} catch (err) {
			// Unexpected throw (rejected promise without {success:false}
			// envelope, e.g. an exception inside the service module
			// itself). Without this catch the loading skeleton would
			// stick forever.
			if (unmountedRef.current) return;
			console.error("[RealtorDashboard] load() failed", err);
			setState({
				status: "error",
				message: t("realtor.dashboard.loadFailedFallback"),
			});
		}
	}, [t]);

	useEffect(() => {
		unmountedRef.current = false;
		load();
		return () => {
			unmountedRef.current = true;
		};
	}, [load]);

	if (state.status === "loading") {
		return (
			<div
				className="p-6 space-y-6 bg-dashboard-surface min-h-screen"
				data-testid="realtor-dashboard-loading"
			>
				<Skeleton className="h-24 w-full rounded-xl bg-dashboard-bg" />
				<Skeleton className="h-20 w-full rounded-xl bg-dashboard-bg" />
				<Skeleton className="h-32 w-full rounded-xl bg-dashboard-bg" />
			</div>
		);
	}

	if (state.status === "error") {
		return (
			<div className="p-6 space-y-6 bg-dashboard-surface min-h-screen">
				<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-5 flex items-start gap-3" role="alert">
						<div className="rounded-full bg-dashboard-pill-risk-bg p-2 shrink-0">
							<AlertCircle className="h-4 w-4 text-dashboard-pill-risk" />
						</div>
						<div className="flex-1 space-y-2">
							<h2 className="text-sm font-semibold text-dashboard-text">
								{t("realtor.dashboard.loadFailedTitle")}
							</h2>
							<p className="text-xs text-dashboard-muted">{state.message}</p>
							<Button
								type="button"
								size="sm"
								className="gap-1.5 bg-dashboard-accent hover:bg-dashboard-accent/90 text-dashboard-on-fill rounded-lg"
								onClick={() => load()}
							>
								<RefreshCw className="h-4 w-4" />
								{t("realtor.dashboard.retry")}
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (state.status === "empty") {
		return (
			<div className="p-6 space-y-6 bg-dashboard-surface min-h-screen">
				<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-3">
						<h2 className="text-lg font-semibold text-dashboard-text font-display">
							{t("realtor.dashboard.completeProfileTitle")}
						</h2>
						<p className="text-sm text-dashboard-muted">
							{t("realtor.dashboard.completeProfileSubtitle")}
						</p>
						<Button
							type="button"
							onClick={() => navigate("/onboarding/realtor")}
							className="bg-dashboard-accent hover:bg-dashboard-accent/90 text-dashboard-on-fill rounded-[8px] shadow-[var(--dashboard-shadow-primary)]"
						>
							{t("realtor.dashboard.completeProfileCta")}
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<LoadedDashboard
			profile={state.profile}
			listings={state.listings}
			leads={state.leads}
			displayName={
				state.profile.name || user?.phone || t("realtor.dashboard.fallbackName")
			}
		/>
	);
}

function greetingKey(hour: number): string {
	if (hour >= 5 && hour < 12) return "realtor.dashboard.greeting.morning";
	if (hour >= 12 && hour < 17) return "realtor.dashboard.greeting.afternoon";
	return "realtor.dashboard.greeting.evening";
}

// A second past midnight, so the new day's ranges are computed on the new day.
const MIDNIGHT_BUFFER_MS = 1000;

function LoadedDashboard({
	profile,
	listings,
	leads,
	displayName,
}: {
	profile: RealtorProfile;
	listings: Listing[];
	leads: LeadWithLinks[];
	displayName: string;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	// Held in state so the schedule query keys stay stable across renders, and
	// re-derived just after local midnight so a page left open overnight moves
	// on to the new day.
	const [ranges, setRanges] = useState(() => dashboardRanges(new Date()));
	useEffect(() => {
		const rollOver = () => {
			if (Date.now() >= ranges.tomorrowStart.getTime()) {
				setRanges(dashboardRanges(new Date()));
			}
		};
		const untilTomorrow = ranges.tomorrowStart.getTime() - Date.now();
		const timer = setTimeout(
			rollOver,
			Math.max(0, untilTomorrow) + MIDNIGHT_BUFFER_MS,
		);
		// A timer doesn't count time the machine spends asleep, so a laptop
		// opened the next morning also checks when the page is shown again.
		document.addEventListener("visibilitychange", rollOver);
		window.addEventListener("focus", rollOver);
		return () => {
			clearTimeout(timer);
			document.removeEventListener("visibilitychange", rollOver);
			window.removeEventListener("focus", rollOver);
		};
	}, [ranges]);
	const dueQuery = useSchedule({ to: ranges.tomorrowStart });
	const weekQuery = useSchedule({ from: ranges.now, to: ranges.weekEnd });

	const hot = useMemo(() => hotListings(listings, leads), [listings, leads]);

	const isVerified = profile.reraVerified && profile.idVerified;
	const isReviewPending = profile.reviewStatus === "pending";
	const isReviewRejected = profile.reviewStatus === "rejected";

	const activeListingsCount = listings.filter((l) =>
		ACTIVE_LISTING_STATUSES.includes(l.status),
	).length;
	const openLeadsCount = leads.filter(isOpenLead).length;
	const openLead = (leadId: string) => navigate(`/realtor/leads/${leadId}`);

	return (
		<div className="p-6 space-y-8 bg-dashboard-surface min-h-screen">
			{/* Welcome */}
			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6">
					<p style={EYEBROW_STYLE}>{t("realtor.dashboard.eyebrow")}</p>
					<h1
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 36,
							fontWeight: 500,
							fontStyle: "italic",
							color: "var(--color-dashboard-text)",
							marginTop: 8,
							lineHeight: 1.1,
						}}
					>
						{t(greetingKey(ranges.now.getHours()))},{" "}
						<span style={{ color: "var(--color-dashboard-accent-display)" }}>
							{displayName}
						</span>
					</h1>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 14,
							color: "var(--color-dashboard-muted)",
							marginTop: 8,
						}}
					>
						{t("realtor.dashboard.welcomeSubtitle")}
					</p>
				</CardContent>
			</Card>

			{/* Verification banner — only when not yet verified. */}
			{!isVerified && (
				<Card className="border-dashboard-warning-border bg-dashboard-warm-050 rounded-xl shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-5 flex items-start gap-3">
						<div className="rounded-full bg-dashboard-pill-awaiting-bg p-2 shrink-0">
							<ShieldCheck className="h-4 w-4 text-yellow-600" />
						</div>
						<div className="flex-1 space-y-1">
							<p className="text-sm font-semibold text-dashboard-warning">
								{isReviewRejected
									? t("realtor.dashboard.verificationRejectedTitle")
									: isReviewPending && profile.idProofUploaded
										? t("realtor.dashboard.verificationPendingTitle")
										: t("realtor.dashboard.verificationCtaTitle")}
							</p>
							<p className="text-xs text-dashboard-pill-awaiting">
								{isReviewRejected
									? (profile.reviewNotes ??
										t("realtor.dashboard.verificationRejectedSubtitle"))
									: isReviewPending && profile.idProofUploaded
										? t("realtor.dashboard.verificationPendingSubtitle")
										: t("realtor.dashboard.verificationCtaSubtitle")}
							</p>
							{(!profile.idProofUploaded || isReviewRejected) && (
								<Button
									size="sm"
									className="mt-2 h-8 bg-dashboard-accent hover:bg-dashboard-accent/90 text-dashboard-on-fill rounded-[8px] shadow-[var(--dashboard-shadow-primary)]"
									onClick={() => navigate("/realtor/verification")}
								>
									{t("realtor.dashboard.startVerification")}
								</Button>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			<WhatsappConnectionBanner />

			<SectionHeading
				eyebrow={t("realtor.dashboard.atAGlanceEyebrow")}
				title={t("realtor.dashboard.keyMetricsTitle")}
			/>

			{/* Stats strip — clickable tiles drilling into the matching tab.
			    The two schedule tiles show a dash until their query succeeds,
			    rather than a 0 that would read as "nothing booked". */}
			<section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
				<StatTile
					label={t("realtor.dashboard.stats.activeListings")}
					value={activeListingsCount}
					icon={<Layers className="h-3.5 w-3.5" />}
					onClick={() => navigate("/realtor/listings")}
				/>
				<StatTile
					label={t("realtor.dashboard.stats.inPipeline")}
					value={openLeadsCount}
					icon={<Users className="h-3.5 w-3.5" />}
					onClick={() => navigate("/realtor/leads")}
				/>
				<StatTile
					label={t("realtor.dashboard.stats.visitsNext7Days")}
					value={weekQuery.isSuccess ? countVisits(weekQuery.data) : null}
					icon={<Home className="h-3.5 w-3.5" />}
					onClick={() => navigate("/realtor/leads")}
				/>
				<StatTile
					label={t("realtor.dashboard.stats.followUpsDue")}
					value={dueQuery.isSuccess ? countFollowUpsDue(dueQuery.data) : null}
					icon={<BellRing className="h-3.5 w-3.5" />}
					onClick={() => navigate("/realtor/leads")}
				/>
			</section>

			<SectionHeading
				eyebrow={t("realtor.dashboard.quickActionsEyebrow")}
				title={t("realtor.dashboard.quickActionsTitle")}
			/>

			{/* Quick actions */}
			<section className="grid grid-cols-2 gap-3">
				<Button
					className="h-11 justify-start gap-2 bg-dashboard-accent hover:bg-dashboard-accent/90 text-dashboard-on-fill rounded-[8px] shadow-[var(--dashboard-shadow-primary)]"
					onClick={() => navigate("/realtor/listings/new")}
				>
					<Plus className="h-4 w-4" />
					{t("realtor.dashboard.quickActions.newListing")}
				</Button>
				<Button
					className="h-11 justify-start gap-2 text-dashboard-text bg-dashboard-bg hover:bg-dashboard-surface rounded-[8px] shadow-[var(--dashboard-shadow-secondary)] hover:shadow-[var(--dashboard-shadow-secondary-hover)]"
					onClick={() => navigate("/realtor/leads/new")}
				>
					<UserRound className="h-4 w-4" />
					{t("realtor.dashboard.quickActions.addLead")}
				</Button>
			</section>

			<WhatsappReviewCard />

			<FollowUpsQueue />

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<TodaySchedule
					query={dueQuery}
					todayStart={ranges.todayStart}
					onOpenLead={openLead}
				/>
				<EnquiriesInbox />
			</div>

			{/* Hot listings — active listings ranked by open linked leads.
			    Hidden entirely when there are no active listings (never an
			    empty grid). */}
			{hot.length > 0 && (
				<Card
					className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]"
					data-testid="hot-listings"
				>
					<CardContent className="p-5 space-y-3">
						<header className="flex items-center justify-between">
							<div>
								<h2 style={EYEBROW_STYLE}>
									{t("realtor.dashboard.hotListingsTitle")}
								</h2>
								<p className="text-xs text-dashboard-muted">
									{t("realtor.dashboard.hotListingsSubtitle")}
								</p>
							</div>
							<Button
								variant="ghost"
								size="sm"
								className="h-7 px-2 text-xs text-dashboard-muted hover:text-dashboard-text hover:bg-dashboard-surface"
								onClick={() => navigate("/realtor/listings")}
							>
								{t("realtor.dashboard.viewAll")}
							</Button>
						</header>
						<div className="grid grid-cols-2 gap-2">
							{hot.map(({ listing, openLeadCount }) => (
								<div key={listing.id} className="space-y-1">
									<ListingCard
										listing={listing}
										onClick={() => navigate(`/realtor/listings/${listing.id}`)}
									/>
									{openLeadCount > 0 && (
										<p className="px-1 text-[11px] font-medium text-dashboard-accent-dark">
											{t("realtor.dashboard.hotListingOpenLeads", {
												count: openLeadCount,
											})}
										</p>
									)}
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			<ComingUp
				query={weekQuery}
				from={ranges.tomorrowStart}
				onOpenLead={openLead}
				onViewAll={() => navigate("/realtor/leads")}
			/>

			{/* First-run hint when there are zero listings AND zero leads. */}
			{listings.length === 0 && leads.length === 0 && (
				<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-5 space-y-2">
						<div className="flex items-center gap-2">
							<Sparkles className="h-4 w-4 text-dashboard-accent-dark" />
							<h2 className="text-sm font-semibold text-dashboard-text">
								{t("realtor.dashboard.gettingStartedTitle")}
							</h2>
						</div>
						<p className="text-xs text-dashboard-muted leading-relaxed">
							{t("realtor.dashboard.gettingStartedBody")}
						</p>
					</CardContent>
				</Card>
			)}
		</div>
	);
}

function StatTile({
	label,
	value,
	icon,
	onClick,
}: {
	label: string;
	/** `null` while the count is loading or failed to load. */
	value: number | null;
	icon: React.ReactNode;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="text-left rounded-xl border border-dashboard-border bg-dashboard-bg p-4 hover:shadow-[var(--dashboard-shadow-3)] transition-all hover:-translate-y-0.5"
		>
			<div className="flex items-center gap-2 text-dashboard-muted">{icon}</div>
			<p
				style={{
					fontFamily: "var(--font-display)",
					fontSize: 32,
					fontWeight: 500,
					color: "var(--color-dashboard-text)",
					marginTop: 8,
					lineHeight: 1,
				}}
			>
				{value ?? "—"}
			</p>
			<p
				style={{
					fontFamily: "var(--font-body)",
					fontSize: 12,
					color: "var(--color-dashboard-muted)",
					marginTop: 4,
				}}
			>
				{label}
			</p>
		</button>
	);
}
